import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cloud, CloudOff, DownloadCloud, LogIn, LogOut, RefreshCw, Save, X } from 'lucide-react';
import type { DocumentSession } from '../editor/core/session';
import type { EditorDocument } from '../editor/core/model';
import { deserializeProject, serializeProject } from '../editor/persistence/project-format';
import { aggregateCapabilities, subscribeToAddons } from '../editor/addons/registry';
import {
  beginCloudUpload,
  CloudApiError,
  commitCloudVersion,
  createCloudDocument,
  downloadCloudDocument,
  initializeCloudAuth,
  listCloudDocuments,
  loadCloudAccount,
  signInToCloud,
  signOutOfCloud,
  uploadCloudBytes,
  type CloudAccount,
  type CloudAuthState,
  type CloudDocument,
} from './client';

interface CloudLink {
  documentId: string;
  currentVersionId: string | null;
}

interface Props {
  active?: DocumentSession;
  onOpenDocument: (document: EditorDocument) => string;
  onToast: (message: string) => void;
}

const initialAuth: CloudAuthState = { configured: true, authenticated: false };

export function CloudWorkspace({ active, onOpenDocument, onToast }: Props) {
  const [, setAddonRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [auth, setAuth] = useState<CloudAuthState>(initialAuth);
  const [account, setAccount] = useState<CloudAccount | null>(null);
  const [workspaceId, setWorkspaceId] = useState('');
  const [documents, setDocuments] = useState<CloudDocument[]>([]);
  const [links, setLinks] = useState<Record<string, CloudLink>>({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const addonCapabilities = aggregateCapabilities();
  const addonHosts = addonCapabilities.network.map((origin) => {
    try {
      return new URL(origin).host;
    } catch {
      return origin;
    }
  });

  const workspace = useMemo(
    () => account?.workspaces.find((candidate) => candidate.id === workspaceId) ?? account?.workspaces[0],
    [account, workspaceId],
  );

  const refresh = useCallback(async () => {
    const nextAccount = await loadCloudAccount();
    setAccount(nextAccount);
    const nextWorkspaceId = nextAccount.workspaces.some((item) => item.id === workspaceId)
      ? workspaceId
      : nextAccount.workspaces[0]?.id ?? '';
    setWorkspaceId(nextWorkspaceId);
    setDocuments(nextWorkspaceId ? await listCloudDocuments(nextWorkspaceId) : []);
  }, [workspaceId]);

  useEffect(() => {
    let current = true;
    void initializeCloudAuth()
      .then(async (nextAuth) => {
        if (!current) return;
        setAuth(nextAuth);
        if (nextAuth.authenticated) await refresh();
      })
      .catch((reason: unknown) => {
        if (current) setError(reason instanceof Error ? reason.message : 'Could not initialize Maipo Cloud.');
      });
    return () => { current = false; };
  // the auth callback must be
  // consumed exactly once; re-running it on a new `refresh` identity would replay it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => subscribeToAddons(() => setAddonRevision((value) => value + 1)), []);

  useEffect(() => {
    if (!auth.authenticated || !workspaceId) return;
    void listCloudDocuments(workspaceId).then(setDocuments).catch(() => undefined);
  }, [auth.authenticated, workspaceId]);

  const run = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError('');
    try {
      await action();
    } catch (reason) {
      const message = reason instanceof CloudApiError && reason.status === 409
        ? 'Cloud save conflict: open the latest cloud copy before saving again.'
        : reason instanceof Error ? reason.message : 'Cloud operation failed.';
      setError(message);
    } finally {
      setBusy('');
    }
  };

  const saveActive = async () => {
    if (!active || !workspace) return;
    const captured = active.captureRevision();
    const bytes = await serializeProject(captured.document);
    let link = links[active.sessionId];
    if (!link) {
      const remote = await createCloudDocument(workspace.id, captured.document.name);
      link = { documentId: remote.id, currentVersionId: remote.currentVersionId };
    }
    const upload = await beginCloudUpload(workspace.id, link.documentId, bytes.byteLength);
    await uploadCloudBytes(upload, bytes);
    const committed = await commitCloudVersion(
      workspace.id,
      link.documentId,
      upload.versionId,
      link.currentVersionId,
    );
    setLinks((current) => ({
      ...current,
      [active.sessionId]: {
        documentId: link.documentId,
        currentVersionId: committed.currentVersionId,
      },
    }));
    active.markSaved(captured.revision);
    setDocuments(await listCloudDocuments(workspace.id));
    onToast(`Saved “${captured.document.name}” to Maipo Cloud.`);
  };

  const openDocument = async (remote: CloudDocument) => {
    if (!workspace) return;
    const downloaded = await downloadCloudDocument(workspace.id, remote.id);
    const decoded = await deserializeProject(downloaded.bytes);
    const sessionId = onOpenDocument(decoded.document);
    setLinks((current) => ({
      ...current,
      [sessionId]: {
        documentId: remote.id,
        currentVersionId: downloaded.versionId,
      },
    }));
    setOpen(false);
    onToast(`Opened “${remote.name}” from Maipo Cloud.`);
  };

  const creditLabel = workspace?.creditMode === 'UNLIMITED'
    ? 'Unlimited credits'
    : `${String(workspace?.credits ?? 0)} credits`;
  const accountLabel = workspace
    ? `${workspace.name} · ${creditLabel}`
    : 'Maipo Cloud';
  const triggerLabel = auth.authenticated
    ? accountLabel
    : addonHosts.length > 0
      ? addonHosts.join(', ')
      : 'Local only';
  const triggerTitle = auth.authenticated
    ? [
      'Open Maipo Cloud',
      ...addonCapabilities.dataLeavesDevice,
    ].join('\n')
    : addonHosts.length > 0
      ? addonCapabilities.dataLeavesDevice.join('\n') || `Connected addons: ${addonHosts.join(', ')}`
      : 'Local editing is private; sign in only if you want cloud features';

  return (
    <>
      <button
        type="button"
        className={auth.authenticated || addonHosts.length > 0 ? 'cloud-account-button' : 'local-badge cloud-trigger'}
        onClick={() => setOpen(true)}
        title={triggerTitle}
      >
        {auth.authenticated || addonHosts.length > 0 ? <Cloud size={13} /> : <CloudOff size={13} />}
        {triggerLabel}
      </button>
      {open ? <div className="modal-backdrop cloud-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
        <section className="dialog cloud-dialog" role="dialog" aria-modal="true" aria-labelledby="cloud-title">
          <div className="dialog-heading">
            <div><span className="eyebrow">Optional account</span><h2 id="cloud-title">Maipo Cloud</h2></div>
            <button type="button" className="icon-button" onClick={() => setOpen(false)} aria-label="Close"><X size={17} /></button>
          </div>
          {!auth.configured ? <>
            <div className="cloud-local-message"><CloudOff size={22} /><div><strong>Local editing stays available</strong><p>Cloud sign-in is not configured in this build. Your browser storage and file saves work exactly as before.</p></div></div>
          </> : !auth.authenticated ? <>
            <div className="cloud-local-message"><Cloud size={22} /><div><strong>Keep local, add cloud when useful</strong><p>Sign in to save versioned project copies and use metered Recraft generation. Local recovery is never replaced.</p></div></div>
            <div className="dialog-actions"><button type="button" className="primary" onClick={() => void run('Signing in…', signInToCloud)}><LogIn size={14} /> Sign in</button></div>
          </> : <>
            <div className="cloud-account-row">
              <label>Workspace<select value={workspace?.id ?? ''} onChange={(event) => setWorkspaceId(event.target.value)}>
                {account?.workspaces.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
              </select></label>
              <div><strong>{workspace?.creditMode === 'UNLIMITED' ? '∞' : String(workspace?.credits ?? 0)}</strong><span>credits</span></div>
              <span className={`cloud-entitlement entitlement-${(workspace?.entitlement ?? 'SUSPENDED').toLowerCase()}`}>{workspace?.entitlement ?? 'SUSPENDED'}</span>
            </div>
            <div className="cloud-actions">
              <button type="button" className="primary" disabled={!active || Boolean(busy)} onClick={() => void run('Saving project…', saveActive)}><Save size={14} /> Save current to cloud</button>
              <button type="button" disabled={Boolean(busy)} onClick={() => void run('Refreshing…', refresh)}><RefreshCw size={14} /> Refresh</button>
            </div>
            <div className="cloud-document-heading"><strong>Cloud documents</strong><span>{documents.length}</span></div>
            <div className="cloud-document-list">
              {documents.length === 0 ? <p>No cloud documents yet. Your local documents have not been uploaded.</p> : documents.map((item) => (
                <button type="button" key={item.id} disabled={Boolean(busy) || !item.currentVersionId} onClick={() => void run('Opening project…', () => openDocument(item))}>
                  <DownloadCloud size={16} /><span><strong>{item.name}</strong><small>{item.currentVersionId ? `Updated ${new Date(item.updatedAt).toLocaleString()}` : 'No saved version'}</small></span>
                </button>
              ))}
            </div>
            <div className="cloud-footer">
              <span>{account?.user.email ?? account?.user.displayName ?? account?.user.id}</span>
              <button type="button" disabled={Boolean(busy)} onClick={() => void run('Signing out…', signOutOfCloud)}><LogOut size={13} /> Sign out</button>
            </div>
          </>}
          {busy ? <p className="cloud-status">{busy}</p> : null}
          {error ? <p className="form-error">{error}</p> : null}
        </section>
      </div> : null}
    </>
  );
}
